// Your sign-in (decision 20, QA-093, QA-092): everyone's own sign-in, from the header. Two-factor
// with an authenticator app (set up, recovery codes, turn off), the devices that skip it for 30 days,
// the mobile that gets text codes, linking Google or Microsoft, and a code to pair an iPad. Every
// change goes through the api's /me routes, which check a current code where one is due.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { RpcError, type SignInMethod } from '@flowology/client';
import { client, setCsrf } from '../api.ts';
import { SetPassword } from './SetPassword.tsx';
import { Header, type NavProps } from '../Nav.tsx';
import { useDesign } from '../blueprint/design.ts';
import { Section } from '@flowology/ui-web';
import { Moved } from '../blueprint/Moved.tsx';
import { deadlineBanner, groupedSecret, qrPath, recoveryNote, type TwoFactorState } from './signin-model.ts';
import { primeTwoFactorState } from './TwoFactorBanner.tsx';
import './signin.css';
import '@flowology/ui-web/base.css';
import '../blueprint/setup.css';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

interface RememberedDevice {
  id: string;
  rememberedAt: string;
  lastUsedAt: string | null;
  until: string;
  thisDevice: boolean;
}

export function SignInScreen({
  nav,
  timeZone,
  usedRecovery,
  onState,
}: {
  nav: NavProps;
  timeZone: string;
  /** Just signed in with a recovery code: this many are left. */
  usedRecovery?: number;
  /** Each fresh read of where the person stands (the app holds its screens while blocked). */
  onState?: (s: TwoFactorState) => void;
}) {
  const [state, setState] = useState<TwoFactorState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      const s = await client.me<TwoFactorState>('GET', '/me/2fa');
      primeTwoFactorState(s);
      setState(s);
      onState?.(s);
      setError(null);
    } catch (e) {
      setError(message(e));
    }
  }, [onState]);
  useEffect(() => void load(), [load]);
  // Which ways in the person has (a password, Google, Microsoft), for those parts.
  const [ways, setWays] = useState<Ways | null>(null);
  const loadWays = useCallback(
    () =>
      client
        .me<Ways>('GET', '/me/sign-in')
        .then(setWays)
        .catch(() => setWays(null)),
    [],
  );
  useEffect(() => void loadWays(), [loadWays]);
  const banner = state ? deadlineBanner(state) : null;
  const bp = useDesign() === 'blueprint';
  const signingIn = (
    <>
      {ways?.password && (
        <Password
          minLength={ways.minLength}
          changedAt={ways.passwordChangedAt}
          timeZone={timeZone}
          onChanged={loadWays}
        />
      )}
      {state ? <TwoFactor state={state} onChanged={load} /> : !error && <p className="muted">Loading…</p>}
      {/* a mobile lives on a staff profile: someone without one has none to confirm */}
      {ways?.mobile && (
        <Mobile mobile={ways.mobile} onChanged={() => void Promise.all([loadWays(), load()])} />
      )}
      <Linked ways={ways} onChanged={loadWays} />
    </>
  );
  return (
    <div className={bp ? 'app bp-setup flw' : 'app'}>
      <Header
        nav={nav}
        title="Your sign-in"
        lede="How you sign in, and the second step that keeps it yours."
      />
      <main className="page signin">
        {banner && (
          <p className={`banner ${banner.tone}`} role={banner.tone === 'bad' ? 'alert' : 'status'}>
            <b>{banner.text}</b> <span className="detail">{banner.detail}</span>
          </p>
        )}
        {usedRecovery !== undefined && (
          <p className={`banner ${usedRecovery <= 2 ? 'warn' : 'info'}`} role="status">
            {recoveryNote(usedRecovery)}
          </p>
        )}
        {error && <p className="error">{error}</p>}
        {ways?.setPasswordUntil && (
          <SetPassword
            until={ways.setPasswordUntil}
            minLength={ways.minLength}
            hasPassword={ways.password}
            timeZone={timeZone}
            onChanged={loadWays}
          />
        )}
        {bp ? (
          // Blueprint (setup.js's sign-in): how you sign in on the left; on the right where you're
          // signed in (the devices, and the AI tools that act with your sign-in), parts on hairlines
          <>
            <div className="bp-grid2">
              <Section title="Signing in">{signingIn}</Section>
              <Section title="Where you’re signed in">
                {state?.on && <Remembered timeZone={timeZone} />}
                {ways?.technician && <PairIpad />}
                {/* the AI tools are a page of their own in Setup (the setup audit) */}
                <Moved what="Your AI tools" to="Setup › Your AI tools" href="#/aitools" />
              </Section>
            </div>
          </>
        ) : (
          <>
            {ways?.password && (
              <Password
                minLength={ways.minLength}
                changedAt={ways.passwordChangedAt}
                timeZone={timeZone}
                onChanged={loadWays}
              />
            )}
            {state ? (
              <TwoFactor state={state} onChanged={load} />
            ) : (
              !error && <p className="muted">Loading…</p>
            )}
            {state?.on && <Remembered timeZone={timeZone} />}
            {/* a mobile lives on a staff profile: someone without one has none to confirm */}
            {ways?.mobile && (
              <Mobile mobile={ways.mobile} onChanged={() => void Promise.all([loadWays(), load()])} />
            )}
            <Linked ways={ways} onChanged={loadWays} />
            {ways?.technician && <PairIpad />}
            <AiTools timeZone={timeZone} />
          </>
        )}
      </main>
    </div>
  );
}

/** Setup › Your AI tools (Blueprint): the AI tools that act with your sign-in, on a page of their own. */
export function AiToolsScreen({ nav, timeZone }: { nav: NavProps; timeZone: string }) {
  return (
    <div className="app bp-setup flw">
      <Header
        nav={nav}
        title="Your AI tools"
        lede="Claude or another AI tool, connected with your own sign-in: it does what you can do."
      />
      <main className="page signin">
        <AiTools timeZone={timeZone} zone />
      </main>
    </div>
  );
}

/**
 * Any other place, past a required two-factor's deadline: the api holds it (QA-089), so the screen
 * isn't drawn at all; the header's banner says why and opens Your sign-in.
 */
export function TwoFactorHold({ nav }: { nav: NavProps }) {
  return (
    <div className="app">
      <Header nav={nav} title="Two-factor first" lede="Everything opens again once two-factor is on." held />
      <main className="page signin">
        <section className="sheet card signin-part" aria-label="Two-factor first">
          <p>Your company needs two-factor before anything else opens.</p>
          <button className="btn primary" onClick={() => nav.onView('signin')}>
            Set up two-factor
          </button>
        </section>
      </main>
    </div>
  );
}

function Part({ title, zone = false, children }: { title: string; zone?: boolean; children: ReactNode }) {
  const bp = useDesign() === 'blueprint';
  // on a page of its own (Your AI tools), the part is the page's section
  if (bp && zone) return <Section title={title}>{children}</Section>;
  // In Blueprint a part sits in its section, under a hairline: a section inside a section never gets
  // a second rule or a box (DESIGN.md §3a)
  if (bp)
    return (
      <div className="bp-part signin-part" role="group" aria-label={title}>
        <h3>{title}</h3>
        {children}
      </div>
    );
  return (
    <section className="sheet card signin-part" aria-label={title}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

interface Ways {
  /** A technician's staff profile: they pair iPads (the server's rule, QA-303, QA-420). */
  technician: boolean;
  password: boolean;
  /** When the password was last set or changed (Last changed …). */
  passwordChangedAt: string | null;
  linked: { google: boolean; microsoft: boolean };
  minLength: number;
  /** Signed in by an emailed code just now: a new password without the old one, until then. */
  setPasswordUntil: string | null;
  /** The profile's mobile by its last four, and whether it's the verified one (BL-102). */
  mobile: { tail: string | null; verified: boolean } | null;
}

/** The api asks for a fresh second factor first (a code from the app, or a recovery code). */
const askedForStepUp = (e: unknown) =>
  e instanceof RpcError && (e.detail as { reason?: string } | undefined)?.reason === 'step_up_required';

/**
 * Change your password (QA-093): the current one, a new one, and a code from the app when the api
 * asks for one. Every other browser and iPad is signed out; this one stays.
 */
function Password({
  minLength,
  changedAt,
  timeZone,
  onChanged,
}: {
  minLength: number;
  changedAt: string | null;
  timeZone: string;
  onChanged: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [code, setCode] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const width = useRef<number | null>(null); // "Changing…" keeps the button's width
  const missing = !current
    ? 'Type your current password.'
    : next.length < minLength
      ? `The new one needs at least ${minLength} characters.`
      : needCode && !code.trim()
        ? 'Type a code from your app, or a recovery code.'
        : null;
  const change = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.me<{ csrf?: string; signedOut: number }>('POST', '/me/password', {
        current,
        next,
        ...(code.trim() ? { code: code.trim() } : {}),
      });
      // Every session of theirs ended with the change (QA-311); this browser has a new one.
      if (r.csrf) setCsrf(r.csrf);
      setDone(
        r.signedOut
          ? 'Password changed. Your other browsers and iPads were signed out.'
          : 'Password changed.',
      );
      void onChanged();
      setOpen(false);
      setCurrent('');
      setNext('');
      setCode('');
      setNeedCode(false);
    } catch (e) {
      if (askedForStepUp(e)) setNeedCode(true);
      setCode('');
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Part title="Password">
      {changedAt && (
        <p className="muted small">
          Last changed{' '}
          {new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium' }).format(new Date(changedAt))}
        </p>
      )}
      {done && (
        <p className="notice ok" role="status">
          {done}
        </p>
      )}
      {!open ? (
        <button
          className="btn soft"
          onClick={() => {
            setOpen(true);
            setDone(null);
          }}
        >
          Change password
        </button>
      ) : (
        <div className="setup">
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <label className="f">
            Current password
            <input
              className="field"
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </label>
          <label className="f">
            New password
            <input
              className="field"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              aria-describedby="pw-rule pw-missing"
              aria-invalid={next.length > 0 && next.length < minLength}
            />
          </label>
          <p className="small muted" id="pw-rule">
            At least {minLength} characters. Your other browsers and iPads will be signed out.
          </p>
          {needCode && (
            <CodeField label="A code from your app, or a recovery code" value={code} onChange={setCode} />
          )}
          <div className="row">
            <button
              className="btn primary"
              disabled={busy || !!missing}
              onClick={() => void change()}
              style={busy ? { minWidth: width.current ?? undefined } : undefined}
              ref={(el) => {
                if (el && !busy) width.current = el.offsetWidth;
              }}
            >
              {busy ? 'Changing…' : 'Change password'}
            </button>
            <button
              className="btn quiet"
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
          {/* the states pass: a primary that can't go yet says what's missing, announced as it changes
              (QA-425): the live region stays mounted and only its words change */}
          <p className="small muted" id="pw-missing" role="status" aria-live="polite">
            {missing && !busy ? missing : ''}
          </p>
        </div>
      )}
    </Part>
  );
}

/** A code from the app (or a recovery code), asked for before a change that needs one. */
function CodeField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="f">
      {label}
      <input
        className="field code"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  return (
    <div className="recovery" role="region" aria-label="Your recovery codes">
      <p>
        <b>Save these recovery codes somewhere safe.</b> Each signs you in once if you lose your phone. They
        aren’t shown again.
      </p>
      <ol className="codes" aria-label="Recovery codes">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ol>
      <div className="row">
        <button className="btn soft" onClick={() => void navigator.clipboard?.writeText(codes.join('\n'))}>
          Copy them
        </button>
        <button className="btn primary" onClick={onDone}>
          I’ve saved them
        </button>
      </div>
    </div>
  );
}

function TwoFactor({ state, onChanged }: { state: TwoFactorState; onChanged: () => Promise<void> }) {
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [asking, setAsking] = useState<'off' | 'codes' | null>(null);
  // Blueprint: one action on the row, "Change it", which opens the two choices (setup.js's sign-in)
  const bp = useDesign() === 'blueprint';
  const [choosing, setChoosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const act = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  if (codes)
    return (
      <Part title="Two-factor sign-in">
        <RecoveryCodes
          codes={codes}
          onDone={() => {
            setCodes(null);
            void onChanged();
          }}
        />
      </Part>
    );
  if (!state.on || state.resetHere)
    return (
      <Part title="Two-factor sign-in">
        <p className="muted">
          {state.resetHere
            ? 'The owner reset your two-factor here. Set up your authenticator app again.'
            : 'Off. After your password or a code, you’ll also give a 6-digit code from an authenticator app on your phone.'}
        </p>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {!setup ? (
          <button
            className="btn primary"
            disabled={busy}
            onClick={() =>
              void act(async () =>
                setSetup(await client.me<{ secret: string; uri: string }>('POST', '/me/2fa/app/start')),
              )
            }
          >
            Set up an authenticator app
          </button>
        ) : (
          <div className="setup">
            <h3>Scan this with your app</h3>
            <p className="muted">
              Google Authenticator, Microsoft Authenticator, 1Password… Or type the key. It’s shown once.
            </p>
            <QrCode uri={setup.uri} />
            <p className="secret" aria-label="Key">
              {groupedSecret(setup.secret)}
            </p>
            <CodeField label="The 6-digit code your app shows" value={code} onChange={setCode} />
            <button
              className="btn primary"
              disabled={busy || !/^\d{6}$/.test(code.trim())}
              onClick={() =>
                void act(async () => {
                  const r = await client.me<{ recoveryCodes: string[] }>('POST', '/me/2fa/app/confirm', {
                    code: code.trim(),
                  });
                  setSetup(null);
                  setCode('');
                  setCodes(r.recoveryCodes);
                  await onChanged(); // on now: the deadline banner goes
                })
              }
            >
              Turn on two-factor
            </button>
          </div>
        )}
      </Part>
    );
  return (
    <Part title="Two-factor sign-in">
      <p>
        <span className="chip ok">On</span> with {state.app ? 'your authenticator app' : 'text codes'}.{' '}
        <span className="muted">
          {state.recoveryLeft} recovery {state.recoveryLeft === 1 ? 'code' : 'codes'} left.
        </span>
      </p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {asking ? (
        <div className="setup">
          <CodeField label="A code from your app, or a recovery code" value={code} onChange={setCode} />
          <div className="row">
            <button
              className={`btn ${asking === 'off' ? 'danger' : 'primary'}`}
              disabled={busy || !code.trim()}
              onClick={() =>
                void act(async () => {
                  if (asking === 'codes') {
                    const r = await client.me<{ recoveryCodes: string[] }>('POST', '/me/2fa/recovery/new', {
                      code: code.trim(),
                    });
                    setCodes(r.recoveryCodes);
                  } else {
                    await client.me('POST', '/me/2fa/off', { code: code.trim() });
                    await onChanged();
                  }
                  setAsking(null);
                  setCode('');
                })
              }
            >
              {asking === 'off' ? 'Turn it off' : 'Make new recovery codes'}
            </button>
            <button className="btn quiet" onClick={() => setAsking(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : bp && !choosing ? (
        <div className="row">
          <button className="btn soft" onClick={() => setChoosing(true)}>
            Change it
          </button>
        </div>
      ) : (
        <div className="row">
          <button className="btn soft" onClick={() => setAsking('codes')}>
            New recovery codes
          </button>
          {!state.required && (
            <button className="btn quiet" onClick={() => setAsking('off')}>
              Turn off
            </button>
          )}
        </div>
      )}
      {state.required && (
        <p className="small muted">Your company requires two-factor for you, so it stays on.</p>
      )}
    </Part>
  );
}

function QrCode({ uri }: { uri: string }) {
  const q = qrPath(uri);
  return (
    <svg
      className="qr"
      viewBox={`0 0 ${q.size} ${q.size}`}
      role="img"
      aria-label="QR code for your authenticator app"
      shapeRendering="crispEdges"
    >
      <rect width={q.size} height={q.size} fill="#fff" />
      <path d={q.d} fill="#000" />
    </svg>
  );
}

function Remembered({ timeZone }: { timeZone: string }) {
  const [devices, setDevices] = useState<RememberedDevice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setDevices((await client.me<{ devices: RememberedDevice[] }>('GET', '/me/2fa/devices')).devices);
    } catch (e) {
      setError(message(e));
    }
  }, []);
  useEffect(() => void load(), [load]);
  const day = (iso: string) =>
    new Intl.DateTimeFormat('en-CA', { timeZone, dateStyle: 'medium' }).format(new Date(iso));
  const forget = async (id?: string) => {
    try {
      await client.me('POST', '/me/2fa/devices/forget', id ? { id } : {});
      await load();
    } catch (e) {
      setError(message(e));
    }
  };
  return (
    <Part title="Remembered devices">
      <p className="muted">These skip the second step for 30 days. Forget one you don’t use any more.</p>
      {error && <p className="error">{error}</p>}
      {devices === null ? (
        <p className="muted">Loading…</p>
      ) : devices.length ? (
        <>
          <ul className="rows">
            {devices.map((d) => (
              <li key={d.id} className="row between">
                <span>
                  <b>{d.thisDevice ? 'This device' : 'A browser'}</b>
                  <span className="muted small">
                    {' '}
                    · remembered {day(d.rememberedAt)}
                    {d.lastUsedAt ? `, last used ${day(d.lastUsedAt)}` : ''} · until {day(d.until)}
                  </span>
                </span>
                <button className="btn quiet" onClick={() => void forget(d.id)}>
                  Forget
                </button>
              </li>
            ))}
          </ul>
          {devices.length > 1 && (
            <button className="btn soft" onClick={() => void forget()}>
              Forget them all
            </button>
          )}
        </>
      ) : (
        <p className="muted">No devices are remembered.</p>
      )}
    </Part>
  );
}

/**
 * The mobile that gets text codes (BL-102): with none on the profile, or to change it, the person
 * types one and gets a code; the profile takes it only once the code is confirmed, so a typo never
 * replaces the mobile they had.
 */
function Mobile({ mobile, onChanged }: { mobile: NonNullable<Ways['mobile']>; onChanged: () => void }) {
  const [sent, setSent] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // typing a mobile: there's none on file, or they asked to use a different one
  const [other, setOther] = useState(false);
  const [phone, setPhone] = useState('');
  const onFile = mobile.tail;
  const typing = !onFile || other;
  const start = (body?: { phone: string }) =>
    void client
      .me<{ phoneTail: string }>('POST', '/me/mobile/start', body)
      .then((r) => {
        setSent(r.phoneTail);
        setCode('');
        setError(null);
      })
      .catch((e: unknown) => setError(message(e)));
  const different = (
    <button
      type="button"
      className="btn quiet"
      onClick={() => {
        setOther(true);
        setSent(null);
        setDone(false);
        setError(null);
      }}
    >
      Use a different mobile
    </button>
  );
  return (
    <Part title="Your mobile">
      <p className="muted">
        Sign-in codes and second-step codes can come by text to your mobile, once you confirm it.
      </p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {done ? (
        <>
          <p className="notice ok" role="status">
            Your mobile ending {sent} is verified. Codes can come to it by text.
          </p>
          <div className="row">{different}</div>
        </>
      ) : sent ? (
        <div className="setup">
          <p className="notice ok" role="status">
            We’ve texted a code to your mobile ending {sent}.
          </p>
          <CodeField label="Code from the text" value={code} onChange={setCode} />
          <div className="row">
            <button
              className="btn primary"
              disabled={!/^\d{6}$/.test(code.trim())}
              onClick={() =>
                void client
                  .me('POST', '/me/mobile/confirm', { code: code.trim() })
                  .then(() => {
                    setDone(true);
                    setOther(false);
                    setPhone('');
                    setError(null);
                    onChanged();
                  })
                  .catch((e: unknown) => setError(message(e)))
              }
            >
              Confirm
            </button>
            {different}
          </div>
        </div>
      ) : typing ? (
        <form
          className="setup"
          onSubmit={(e) => {
            e.preventDefault();
            start({ phone: phone.trim() });
          }}
        >
          <label className="f">
            Mobile number (Canadian or US)
            <input
              className="field"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              placeholder="604-555-0123"
              maxLength={40}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <div className="row">
            <button type="submit" className="btn primary" disabled={!phone.trim()}>
              Text me a code
            </button>
            {onFile && (
              <button type="button" className="btn quiet" onClick={() => setOther(false)}>
                Cancel
              </button>
            )}
          </div>
        </form>
      ) : (
        <>
          <p>
            {mobile.verified
              ? `Your mobile ending ${onFile} is verified.`
              : `The mobile on your profile ends ${onFile}. Confirm it to get codes by text.`}
          </p>
          <div className="row">
            {!mobile.verified && (
              <button className="btn soft" onClick={() => start()}>
                Confirm your mobile
              </button>
            )}
            {different}
          </div>
        </>
      )}
    </Part>
  );
}

/** Google or Microsoft as another way in, where the company allows it (decision 19, 20). */
function Linked({ ways, onChanged }: { ways: Ways | null; onChanged: () => Promise<void> }) {
  const [methods, setMethods] = useState<readonly SignInMethod[]>([]);
  const [notice, setNotice] = useState<string | null>(() => {
    const q = new URLSearchParams(window.location.search);
    for (const p of ['google', 'microsoft'] as const) {
      const v = q.get(p);
      if (!v) continue;
      window.history.replaceState(null, '', window.location.pathname);
      const name = p === 'google' ? 'Google' : 'Microsoft';
      return v === 'linked'
        ? `${name} is linked: you can sign in with it.`
        : `That ${name} account is already someone else’s here.`;
    }
    return null;
  });
  const [error, setError] = useState<string | null>(null);
  // Unlinking asks once ("Unlink Google?"), and for a fresh second factor when the person has one (QA-093).
  const [unlinking, setUnlinking] = useState<'google' | 'microsoft' | null>(null);
  const [needCode, setNeedCode] = useState(false);
  const [code, setCode] = useState('');
  // Linking asks for a fresh second factor when the person has one (QA-200): the code is confirmed
  // (a step-up), then the provider opens. (Every hook sits above the early return below: React #310.)
  const [linking, setLinking] = useState<'google' | 'microsoft' | null>(null);
  const [linkCode, setLinkCode] = useState('');
  useEffect(() => {
    client
      .signInMethods()
      .then((r) => setMethods(r.methods))
      .catch(() => setMethods([]));
  }, []);
  const linked = ways?.linked;
  const providers = (['google', 'microsoft'] as const).filter((p) => methods.includes(p) || linked?.[p]);
  if (!providers.length && !notice) return null;
  const link = async (p: 'google' | 'microsoft') => {
    setError(null);
    try {
      if (linking === p && linkCode.trim())
        await client.me('POST', '/me/2fa/step-up', { code: linkCode.trim() });
      const r = await client.me<{ url: string }>('POST', `/auth/${p}/link`);
      window.location.assign(r.url);
    } catch (e) {
      setLinking(askedForStepUp(e) ? p : null);
      setLinkCode('');
      setError(message(e));
    }
  };
  const unlink = async (p: 'google' | 'microsoft') => {
    setError(null);
    try {
      await client.me('POST', '/me/sign-in/unlink', {
        provider: p,
        ...(code.trim() ? { code: code.trim() } : {}),
      });
      setUnlinking(null);
      setNeedCode(false);
      setCode('');
      setNotice(`${p === 'google' ? 'Google' : 'Microsoft'} is unlinked.`);
      await onChanged();
    } catch (e) {
      // the refusal ("This is your only way to sign in…") or the code ask stays in the confirm
      if (askedForStepUp(e)) setNeedCode(true);
      setCode('');
      setError(message(e));
    }
  };
  return (
    <Part title="Linked sign-ins">
      {notice && (
        <p className="notice ok" role="status">
          {notice}
        </p>
      )}
      {error && !unlinking && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      <ul className="rows">
        {providers.map((p) => {
          const name = p === 'google' ? 'Google' : 'Microsoft';
          return (
            <li key={p} className="row between">
              {/* as the prototype's rows: the name, then what it means for signing in */}
              <span className="row-text">
                <b>{name}</b>
                <span className="muted small">{linked?.[p] ? 'You can sign in with it' : 'Not linked'}</span>
              </span>
              {linked?.[p] ? (
                <span className="row">
                  <span className="chip ok">Linked</span>
                  <button
                    className="btn quiet"
                    onClick={() => {
                      setUnlinking(p);
                      setNeedCode(false);
                      setCode('');
                      setError(null);
                      setNotice(null);
                    }}
                  >
                    Unlink
                  </button>
                </span>
              ) : (
                <button className="btn soft" onClick={() => void link(p)}>
                  Link {name}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {linking && (
        <div
          className="setup"
          role="group"
          aria-label={`Link ${linking === 'google' ? 'Google' : 'Microsoft'}`}
        >
          <CodeField
            label="A code from your app, or a recovery code"
            value={linkCode}
            onChange={setLinkCode}
          />
          <div className="row">
            <button className="btn primary" disabled={!linkCode.trim()} onClick={() => void link(linking)}>
              Link {linking === 'google' ? 'Google' : 'Microsoft'}
            </button>
            <button
              className="btn quiet"
              onClick={() => {
                setLinking(null);
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {unlinking && (
        <div
          className="setup"
          role="group"
          aria-label={`Unlink ${unlinking === 'google' ? 'Google' : 'Microsoft'}`}
        >
          <p>
            <b>Unlink {unlinking === 'google' ? 'Google' : 'Microsoft'}?</b>{' '}
            {`You won’t be able to sign in with ${unlinking === 'google' ? 'Google' : 'Microsoft'} until you link it again.`}
          </p>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          {needCode && (
            <CodeField label="A code from your app, or a recovery code" value={code} onChange={setCode} />
          )}
          <div className="row">
            <button
              className="btn primary"
              disabled={needCode && !code.trim()}
              onClick={() => void unlink(unlinking)}
            >
              Unlink {unlinking === 'google' ? 'Google' : 'Microsoft'}
            </button>
            <button
              className="btn quiet"
              onClick={() => {
                setUnlinking(null);
                setNeedCode(false);
                setError(null);
              }}
            >
              Keep it
            </button>
          </div>
        </div>
      )}
    </Part>
  );
}

/** QA-092: a code the iPad takes instead of a sign-in (for companies that sign in only with a provider). */
function PairIpad() {
  const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A new code ends the old one (QA-092).
  const make = () =>
    client
      .me<{ code: string; expiresAt: string }>('POST', '/me/devices/pairing-code')
      .then((c) => {
        setCode(c);
        setError(null);
      })
      .catch((e: unknown) =>
        setError(
          e instanceof RpcError &&
            (e.detail as { reason?: string } | undefined)?.reason === 'two_factor_setup_required'
            ? 'Set up two-factor above first: your company requires it before an iPad is paired.'
            : message(e),
        ),
      );
  return (
    <Part title="Pair an iPad">
      <p className="muted">For the tech app: a short code to type on the iPad instead of signing in there.</p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {code ? (
        <>
          <p className="pair">
            <code className="pair-code">{code.code}</code>
          </p>
          <p className="muted">
            Type it on the iPad with your company’s name. It works once, for 10 minutes.
          </p>
          <button className="btn soft" onClick={() => void make()}>
            Make a new code
          </button>
        </>
      ) : (
        <button className="btn soft" onClick={() => void make()}>
          Make a pairing code
        </button>
      )}
    </Part>
  );
}

interface AiTool {
  id: string;
  label: string | null;
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  actsAsYou: boolean;
}

/**
 * Connect an AI tool (DEC-009): Claude or another MCP client, with your own sign-in. One that acts as
 * you does what you may do without asking, except what you always confirm here; one that doesn't asks
 * first, like an agent. The key is shown once.
 */
function AiTools({ timeZone, zone = false }: { timeZone: string; zone?: boolean }) {
  const [tools, setTools] = useState<AiTool[] | null>(null);
  const [label, setLabel] = useState('Claude');
  const [actsAsYou, setActsAsYou] = useState(true);
  const [days, setDays] = useState(30);
  const [made, setMade] = useState<{ key: string; actsAsYou: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const day = (iso: string) => new Date(iso).toLocaleDateString('en-CA', { dateStyle: 'medium', timeZone });
  const load = useCallback(
    () =>
      client
        .me<{ tools: AiTool[] }>('GET', '/me/ai-tools')
        .then((r) => setTools(r.tools))
        .catch((e: unknown) => setError(message(e))),
    [],
  );
  useEffect(() => void load(), [load]);
  const run = async (f: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await f();
      setError(null);
      await load();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Part title="AI tools" zone={zone}>
      <p className="muted">
        Connect Claude or another AI tool to Flowology with your own sign-in. It can do what you can do. When
        it acts as you, it doesn’t ask first, except for money going out and anything that can’t be undone
        (refunds, voids, write-offs, paying bills, merging customers, closing a month): those always wait for
        you to confirm here. Approving requests, sign-offs, who may do what and payroll are never done by an
        AI tool.
      </p>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {made ? (
        <div className="aitool-made">
          <p>
            <b>Your key.</b> Copy it into the AI tool now: it isn’t shown again.
          </p>
          <p>
            <code className="secret aitool-key">{made.key}</code>
          </p>
          <p className="muted">
            Flowology’s MCP address: <code>{`${location.origin}/mcp`}</code>.{' '}
            {made.actsAsYou ? 'It acts as you.' : 'It asks you first for what it does.'}
          </p>
          <button className="btn soft" onClick={() => void navigator.clipboard?.writeText(made.key)}>
            Copy the key
          </button>{' '}
          <button className="btn quiet" onClick={() => setMade(null)}>
            Done
          </button>
        </div>
      ) : (
        <form
          className="aitool-form"
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              const r = await client.me<{ key: string; actsAsYou: boolean }>('POST', '/me/ai-tools', {
                label,
                actsAsYou,
                days,
              });
              setMade({ key: r.key, actsAsYou: r.actsAsYou });
            });
          }}
        >
          <label>
            Name it <input value={label} maxLength={60} onChange={(e) => setLabel(e.target.value)} />
          </label>
          <label className="check">
            <input type="checkbox" checked={actsAsYou} onChange={(e) => setActsAsYou(e.target.checked)} /> Act
            as me
          </label>
          <label>
            For{' '}
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
          </label>
          <button className="btn" type="submit" disabled={busy || !label.trim()}>
            Connect an AI tool
          </button>
        </form>
      )}
      {tools && tools.length > 0 && (
        <ul className="aitool-list">
          {tools.map((t) => (
            <li key={t.id}>
              <b>{t.label ?? 'AI tool'}</b>{' '}
              <span className="muted">
                {t.actsAsYou ? 'acts as you' : 'asks you first'}, until {day(t.expiresAt)}
                {t.lastUsedAt ? `, last used ${day(t.lastUsedAt)}` : ', not used yet'}
              </span>{' '}
              <button
                className="btn quiet"
                disabled={busy}
                onClick={() =>
                  void run(() => client.me('POST', `/me/ai-tools/${t.id}/acts-as-you`, { on: !t.actsAsYou }))
                }
              >
                {t.actsAsYou ? 'Ask me first instead' : 'Let it act as me'}
              </button>{' '}
              <button
                className="btn quiet"
                disabled={busy}
                onClick={() => void run(() => client.me('POST', `/me/ai-tools/${t.id}/revoke`))}
              >
                Disconnect
              </button>
            </li>
          ))}
        </ul>
      )}
    </Part>
  );
}
